import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';

/* Minimal session hook for Intraop.
 *
 * Returns the current Supabase session (or null if not signed in) plus
 * helpers to sign out and to build a sign-in URL that bounces back
 * here after a successful login on plan.cadtomie.com.
 *
 * We deliberately don't replicate the planner's full `useAuth` hook —
 * Intraop doesn't sign users up, doesn't reset passwords, doesn't
 * resend verification emails. All of that happens on the planner.
 * Intraop just *reads* the session and provides a sign-out and a way
 * to start the SSO flow. */

export interface SessionState {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        if (cancelled) return;
        setSession(newSession);
      },
    );
    return () => {
      cancelled = true;
      listener.subscription.unsubscribe();
    };
  }, []);

  return {
    session,
    loading,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };
}

/** Build the URL that opens the planner's login page and brings the
 *  user back to Intraop after a successful sign-in. */
export function buildSignInUrl(returnTo: string = window.location.href): string {
  const planner = resolvePlannerOrigin();
  return `${planner}/login?return=${encodeURIComponent(returnTo)}`;
}

/** Detect whether we're on prod or dev and return the matching planner
 *  origin. Falls back to plan.cadtomie.com so a misconfigured dev
 *  environment still sends users to a known-good sign-in. */
function resolvePlannerOrigin(): string {
  if (typeof window === 'undefined') return 'https://plan.cadtomie.com';
  // Any native Capacitor shell (iOS = capacitor:, Android = http://localhost)
  // must NOT fall into the localhost dev branch below. RequireSession also
  // gates this away via NativeSignIn, but defence-in-depth: if anything
  // accidentally calls buildSignInUrl() inside the native app, route to
  // the prod planner instead of a dev server that doesn't exist on the
  // user's phone.
  if (Capacitor.isNativePlatform()) return 'https://plan.cadtomie.com';
  const { hostname, protocol } = window.location;
  if (hostname.endsWith('.cadtomie.com')) return 'https://plan.cadtomie.com';
  if (hostname.endsWith('.cadtomie.local')) return `${protocol}//plan.cadtomie.local:5173`;
  if (hostname === 'localhost') return 'http://localhost:5173';
  return 'https://plan.cadtomie.com';
}
